import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const CHECK_INTERVAL_MS = 15 * 60 * 1000;

// The hash of the main bundle currently executing in this tab.
const runningBundleHash = () => {
  const script = [...document.querySelectorAll('script[src]')]
    .map((s) => s.src)
    .find((src) => /\/static\/js\/main\.[a-z0-9]+\.js/.test(src));
  return script ? script.match(/main\.([a-z0-9]+)\.js/)[1] : null;
};

// The hash the server is serving right now. cache:'no-store' bypasses both the
// browser cache and the service worker, so this sees the real deployed build.
const deployedBundleHash = async () => {
  const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
  const html = await res.text();
  const match = html.match(/\/static\/js\/main\.([a-z0-9]+)\.js/);
  return match ? match[1] : null;
};

const applyUpdate = async () => {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration) {
      await registration.update();
      registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
    }
  } catch {
    // fall through to the reload regardless
  }
  window.location.reload();
};

// Detects a newer deployed build and either reloads silently or prompts.
export function useAppUpdate(mode = 'silent') {
  const handled = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (cancelled || handled.current || document.hidden) return;
      const running = runningBundleHash();
      if (!running) return; // dev server: no hashed bundle to compare
      try {
        const deployed = await deployedBundleHash();
        if (!deployed || deployed === running) return;
        handled.current = true;
        if (mode === 'prompt') {
          toast('A new version of Taptile is available', {
            description: 'Reload to get the latest fixes and features.',
            duration: Infinity,
            action: { label: 'Reload', onClick: applyUpdate },
          });
        } else {
          applyUpdate();
        }
      } catch {
        // offline or blocked: try again on the next check
      }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', check);
    };
  }, [mode]);
}
